import { 
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import './style.css';
import Icons from "../icons/iconts";
import { useParams } from "react-router-dom";
import SimpleTable from "../table/table";

function FinanceTimeline({ data = [] }: { data?: any[] }) {
  const navigate = useNavigate();
  const { id } = useParams();

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const formatDate = (date: string) => {
    return moment(date).format("DD/MM/YYYY");
  };

  // Garantir que data é sempre um array
  const safeData = Array.isArray(data) ? data : [];
  
  // Ordenar os dados pela data da despesa de forma decrescente
  const sortedData = safeData.length > 0 
    ? [...safeData].sort((a, b) => {
        const dateA = new Date(a.financialDate || a.financialDateAdded).getTime();
        const dateB = new Date(b.financialDate || b.financialDateAdded).getTime();
        return dateB - dateA; // Ordem decrescente
      }) 
    : [];

  // Definir as colunas da tabela com estilo melhorado
  const columns = [
    {
      id: 'createdAt',
      label: 'Lançamento (Data)',
      render: (value: string) => (
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#666' }}>
          {formatDate(value)}
        </Typography>
      ),
      minWidth: 120
    },
    {
      id: 'financialDateAdded',
      label: 'Despesa (Data)',
      render: (value: string) => (
        <Typography variant="body2" sx={{ color: '#666' }}>
          {value ? formatDate(value) : '-'}
        </Typography>
      ),
      minWidth: 120
    },
    {
      id: 'beneficiary',
      label: 'Beneficiários',
      render: (value: any, row: any) => {
        let beneficiaryText = '';
        if (typeof value === 'string') beneficiaryText = value;
        else if (Array.isArray(value)) beneficiaryText = value.join(', ');
        else if (row.beneficiary) beneficiaryText = row.beneficiary.name || row.beneficiary;
        else beneficiaryText = '-';
        
        return (
          <Chip 
            label={beneficiaryText}
            size="small"
            variant="outlined"
            sx={{ 
              backgroundColor: '#f8f9fa',
              borderColor: '#6ba6b8',
              color: '#333',
              fontSize: '0.75rem'
            }}
          />
        );
      },
      minWidth: 150
    },
    {
      id: 'rubrica',
      label: 'Rubrica',
      render: (value: any) => {
        const rubricaText = value?.name || '-';
        return (
          <Chip 
            label={rubricaText}
            size="small"
            sx={{ 
              backgroundColor: rubricaText !== '-' ? '#e8f4f8' : '#f5f5f5',
              color: rubricaText !== '-' ? '#2c5f6b' : '#999',
              fontSize: '0.75rem',
              fontWeight: 500
            }}
          />
        );
      },
      minWidth: 120
    },
    {
      id: 'valor',
      label: 'Valor',
      render: (value: number) => (
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 600,
            color: '#2c5f6b',
            fontSize: '0.9rem'
          }}
        >
          {formatCurrency(value)}
        </Typography>
      ),
      align: 'right',
      minWidth: 100
    },
    {
      id: 'descricao',
      label: 'Descrição',
      render: (value: string, row: any) => {
        const description = value || row.description || row.lancamento?.description || row.lancamento?.name || '-';
        return (
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#666',
              maxWidth: 200,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
            title={description}
          >
            {description}
          </Typography>
        );
      },
      minWidth: 200
    },
    {
      id: 'actions',
      label: 'Ações',
      align: 'center',
      render: (value: any, row: any) => (
        <Tooltip title="Editar movimentação">
          <IconButton
            size="small"
            onClick={() => navigate(`/financeiro/${row.id}`)}
            sx={{
              color: '#6ba6b8',
              '&:hover': {
                backgroundColor: '#e8f4f8',
                color: '#2c5f6b'
              }
            }}
          >
            <Icons iconName="Edit" fontSize={18} />
          </IconButton>
        </Tooltip>
      ),
      minWidth: 80
    }
  ];

  return (
    <Box className="timeline-container">
      <Box 
        sx={{ 
          color: '#6ba6b8',
          borderRadius: 2,
          marginBottom: 2,
          padding: '16px 24px'
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Icons iconName="AccountBalance" fontSize={28} />
            Movimentação Financeira do Projeto
          </Typography>
          
          <Tooltip title="Gerar Relatório de Movimentações">
            <IconButton
              onClick={() => navigate(`/relatorio-financeiro/${id}`)}
              sx={{
                color: '#6ba6b8',
                backgroundColor: 'rgba(255,255,255,0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.2)'
                }
              }}
            >
              <Icons iconName="ShowChart" fontSize={24} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ borderRadius: 2, overflow: 'hidden', backgroundColor: 'white' }}>
        <Box 
          className="finance-table-container"
          sx={{
            '& .MuiTable-root': {
              '& .MuiTableHead-root': {
                backgroundColor: '#f8f9fa'
              },
              '& .MuiTableHead-root .MuiTableCell-root': {
                fontWeight: 600,
                color: '#2c5f6b',
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              },
              '& .MuiTableBody-root .MuiTableRow-root': {
                '&:hover': {
                  backgroundColor: '#f8f9fa'
                },
                '&:nth-of-type(even)': {
                  backgroundColor: '#fafbfc'
                }
              },
              '& .MuiTableCell-root': {
                borderBottom: '1px solid #e9ecef',
                padding: '12px 16px'
              }
            }
          }}
        >
          <SimpleTable 
            columns={columns} 
            data={sortedData}
          />
        </Box>
      </Box>

      {sortedData.length === 0 && (
        <Box sx={{ backgroundColor: '#f8f9fa', marginTop: 2, borderRadius: 2 }}>
          <Box sx={{ textAlign: 'center', padding: '40px 24px' }}>
            <Box sx={{ color: '#ccc', marginBottom: 2 }}>
              <Icons iconName="Receipt" fontSize={48} />
            </Box>
            <Typography variant="h6" sx={{ color: '#666', marginBottom: 1 }}>
              Nenhuma movimentação encontrada
            </Typography>
            <Typography variant="body2" sx={{ color: '#999' }}>
              As movimentações financeiras do projeto aparecerão aqui
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default FinanceTimeline;